/* eslint-disable react/no-unescaped-entities */
/* eslint-disable import/no-unresolved */
/* eslint-disable no-unused-vars */
import 'rsuite/dist/rsuite.min.css';
import { DateRangePicker } from 'rsuite';
import {
  Box, Button, FormControl, GridLegacy as Grid, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import API from '../utils/apiConfig';
import {
  sec,
} from 'src/utils/tools';
import moment from 'moment';

function calculateCRC16Modbus(buffer) {
  let crc = 0xFFFF;
  for (let pos = 0; pos < buffer.length; pos++) {
    crc ^= buffer[pos];
    for (let i = 0; i < 8; i++) {
      if ((crc & 1) !== 0) {
        crc >>= 1;
        crc ^= 0xA001;
      } else {
        crc >>= 1;
      }
    }
  }
  return crc;
}

const RS485ConfigPage = () => {
  const theme = useTheme();
  const [loggerId, setLoggerId] = useState('');
  const [device, setDevice] = useState();
  const [baudRate, setBaudRate] = useState('19200');
  const [slaveId, setSlaveId] = useState();
  const [startAddress, setStartAddress] = useState();
  const [quantity, setQuantity] = useState();
  const [msgTx, setMsgTx] = useState('');
  const [dates, setDates] = useState([new Date(Date.now() - 1 * 86400000), new Date()]);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    const msg = [
      slaveId & 0xFF, // Slave ID
      0x03, // Function code
      (startAddress >> 8) & 0xFF, // Start high byte
      startAddress & 0xFF, // Start low byte
      (quantity >> 8) & 0xFF, // Quantity high byte
      quantity & 0xFF, // Quantity low byte
    ];

    const crc = calculateCRC16Modbus(Uint8Array.from(msg));
    msg.push(crc & 0xFF); // CRC Low byte
    msg.push((crc >> 8) & 0xFF); // CRC High byte

    const hexString = msg.map((b) => b.toString(16).padStart(2, '0').toUpperCase()).join('');
    setMsgTx(hexString);
  }, [slaveId, startAddress, quantity]);

  const getDevice = async () => {
    try {
      if (loggerId?.trim()?.length === 0) {
        toast.error('Please enter a valid Device ID.');
        return null;
      }
      const r = await API.get(`/v1/devices?id=${loggerId}&config=true`);
      if (r.data.success) {
        const { secret, type } = r.data.data;
        if (!secret) {
          toast.error('Invalid device.');
          return null;
        }
        if (type !== 'ZN_ANCHOR') {
          toast.error('This feature is limited to Anchor loggers.');
          return null;
        }
        setDevice(r.data.data);
        const { RS485_TX, RS485_BAUD_RATE } = r.data.data?.config || {};
        if (RS485_TX) {
          setSlaveId(parseInt(RS485_TX.slice(0, 2), 16));
          setStartAddress(parseInt(RS485_TX.slice(4, 8), 16));
          setQuantity(parseInt(RS485_TX.slice(8, 12), 16));
        }
        if (RS485_BAUD_RATE) {
          setBaudRate(RS485_BAUD_RATE);
        }
        return secret;
      }
      throw new Error(r.data.message);
    } catch (err) {
      console.log('getDevice::', err);
    }
    toast.error("You don’t have access to this device, or it doesn't exist within your organization.");
    return null;
  };

  const handleUpdate = async () => {
    try {
      const r = await API.patch('/v1/devices/config', {
        secrets: [device.secret],
        config: {
          RS485_TX: msgTx,
          RS485_BAUD_RATE: baudRate,
        },
      });
      if (r.data.success) {
        toast.success(`RS485 configuration update request submitted for device ${loggerId}: ${msgTx}`);
        console.log(r.data.data);
      } else {
        throw new Error(r.data.message);
      }
    } catch (err) {
      console.log('handleUpdate::', err);
      toast.error(`Failed to place the request. Error: ${err.message}`);
    }
  };

  const handleFetch = async () => {
    try {
      setTableData([]);
      const { secret } = device;
      const r = await API.get(`/v1/packets?filter=${false}&secret=${secret}&startTime=${sec(dates[0].getTime())}&endTime=${sec(dates[1].getTime())}`);
      if (r.data.success) {
        const data = (r?.data?.data || [])
          .filter((packet) => Object.prototype.hasOwnProperty.call(packet, 'rs485Rx'))
          .map((packet) => ({
            timestamp: sec(parseInt(packet.timestamp, 10)),
            Slave_Response: packet.rs485Rx,
          }))
          .sort((a, b) => b.timestamp - a.timestamp);
        if (data.length === 0) {
          toast.info('No slave responses were found within the specified date range.');
        }
        setTableData(data);
      } else {
        throw new Error(r.data.message);
      }
    } catch (err) {
      console.log('handleFetch::', err);
      toast.error(`Failed to get slave response. Error: ${err.message}`);
    }
  };

  return (
    <>
      <Helmet>
        <title> RS485 Config for Anchor | Mindlabs </title>
      </Helmet>
      <Box sx={{
        background: 'white',
        borderRadius: '10px',
        m: '1rem',
        border: '2px solid #eee',
      }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              padding: '0.6rem 1rem',
              background: theme.palette.primary.lighter,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              Anchor RS485 Config (beta version - for integration purposes only)
            </Typography>
            <span style={{ flexGrow: 1 }} />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              alignItems: 'center', display: 'flex', gap: 3, padding: '0.5rem 2rem',
            }}
          >
            <Typography variant="subtitle1">Logger Details:</Typography>
            <TextField
              id="LOGGER_ID"
              label="Logger ID"
              variant="outlined"
              size="small"
              value={loggerId || ''}
              disabled={!!device}
              placeholder="Enter Logger ID"
              onChange={(e) => setLoggerId(e.target.value?.trim())}
            />
            <Button variant="outlined" onClick={getDevice} disabled={!loggerId || !!device}>
              Set
            </Button>
          </Grid>
          {device && (
            <>
              <Grid item sx={{ paddingX: 4, paddingTop: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>RS485 Configuration:</Typography>
              </Grid>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                width: '100%',
              }}
              >
                <Grid
                  container
                  sx={{
                    width: { md: '60%' }, height: '100%', justifyContent: 'space-between', display: 'flex', gap: 4,
                  }}
                >
                  <Grid container sx={{ flex: 1, display: 'flex', gap: 2 }}>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Typography sx={{ width: '23%' }} variant="subtitle1">Slave Address:</Typography>
                      <FormControl size="small" fullWidth sx={{ maxWidth: '16rem' }}>
                        <Select
                          value={baudRate}
                          onChange={(e) => setBaudRate(e.target.value)}
                        >
                          <MenuItem value="4800">4800</MenuItem>
                          <MenuItem value="9600">9600</MenuItem>
                          <MenuItem value="19200">19200</MenuItem>
                          <MenuItem value="57600">57600</MenuItem>
                          <MenuItem value="115200">115200</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Typography sx={{ width: '23%' }} variant="subtitle1">Slave Address:</Typography>
                      <TextField
                        id="SLAVE_ADDRESS"
                        placeholder="(1 - 247)"
                        variant="outlined"
                        size="small"
                        value={slaveId || ''}
                        onChange={(e) => setSlaveId(e.target.value)}
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Typography sx={{ width: '23%' }} variant="subtitle1">Function Code:</Typography>
                      <Typography sx={{ width: '23%' }} variant="subtitle1">03</Typography>
                      {/* <TextField
                        id="FUNCTION_CODE"
                        placeholder="03"
                        variant="outlined"
                        size="small"
                        value={config.FUNCTION_CODE || '03'}
                        disabled
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (value.length <= 2 && /^[0-9A-F]*$/.test(value)) {
                            setConfig((prev) => ({ ...prev, FUNCTION_CODE: value }));
                          }
                        }}
                      /> */}
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Typography sx={{ width: '23%' }} variant="subtitle1">Start Address:</Typography>
                      <TextField
                        id="START_ADDRESS"
                        placeholder="(0 - 65535)"
                        variant="outlined"
                        size="small"
                        value={startAddress}
                        onChange={(e) => setStartAddress(e.target.value)}
                        type="number"
                        helperText="Raw protocol address starting from 0 in decimal format. E.g., enter 0 to read from 40001."
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Typography sx={{ width: '23%' }} variant="subtitle1">No. of Registers:</Typography>
                      <TextField
                        id="No. of Registers"
                        placeholder="(1 - 10)"
                        variant="outlined"
                        size="small"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        type="number"
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography sx={{ width: '23%' }} variant="subtitle1">Raw msg:</Typography>
                    <Typography sx={{ width: '23%' }} variant="subtitle1">{msgTx}</Typography>
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography fontStyle="italic" sx={{ width: '23%' }} variant="subtitle1">Pending update:</Typography>
                    <Typography fontStyle="italic" sx={{ width: '23%' }} variant="subtitle1">{device.pendingConfig?.RS485_TX || 'NA'}</Typography>
                  </Grid>
                  <Grid
                    container
                    sx={{
                      display: 'flex', justifyContent: 'flex-end', gap: 2, alignContent: 'flex-end',
                    }}
                  >
                    <Button variant="contained" onClick={handleUpdate}>
                      Update
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              <Grid item sx={{ paddingLeft: 4, paddingTop: '1rem' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>RS485 Data:</Typography>
              </Grid>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                width: '100%',
              }}
              >
                <Grid
                  container
                  sx={{
                    width: { md: '60%' }, height: '100%', justifyContent: 'space-between', display: 'flex', gap: 4,
                  }}
                >
                  <Grid
                    item
                    sx={{
                      alignItems: 'center', display: 'flex', gap: 3, width: '100%',
                    }}
                  >
                    <Grid item>
                      <DateRangePicker
                        block
                        format="yyyy-MM-dd HH:mm"
                        value={dates}
                        style={{ zIndex: 9999 }}
                        character=" to "
                        cleanable={false}
                        placement="top"
                        onChange={setDates}
                        editable
                        container={() => document.body}
                        disabledDate={(date) => {
                          const min = new Date('2025-04-04');
                          const max = new Date();
                          max.setDate(max.getDate() + 1);
                          return date < min || date > max;
                        }}
                      />
                    </Grid>
                    <span style={{ flexGrow: 1 }} />
                    <Button variant="outlined" onClick={handleFetch}>
                      Fetch
                    </Button>
                  </Grid>
                  <Grid container>
                    <TableContainer component={Paper} sx={{ border: '1px solid #ddd' }}>
                      <Table hover="true" stickyHeader size="small" sx={{ justifyContent: 'center' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Slave Response</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableData.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Typography
                                sx={{
                                  padding: 3,
                                  textAlign: 'center',
                                  width: '100%',
                                  color: 'grey',
                                }}
                                variant="body2"
                              >
                                Enter the Logger ID, choose a date range, and hit 'Fetch' to load the slave response.
                              </Typography>
                            </TableCell>
                          </TableRow>
                          )}
                          {tableData.length > 0 && tableData.map((row) => (
                            <TableRow key={row.timestamp}>
                              <TableCell>
                                <Typography
                                  sx={{
                                    padding: 1,
                                  }}
                                  variant="body2"
                                >
                                  {moment.unix(parseInt(row.timestamp, 10) || 0).format('HH:mm:ss DD-MM-YYYY')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    padding: 1,
                                  }}
                                  variant="body2"
                                >
                                  {row.Slave_Response}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default RS485ConfigPage;
